import type { Options } from '@contentful/rich-text-react-renderer'
import {
  BLOCKS,
  Block,
  INLINES,
  Inline,
  Text,
} from '@contentful/rich-text-types'
import { AnchorReveal, Figure, Quote } from '~/components-v2'
import { CodeBlock } from '~/components-v2/code-block'
import { VideoFigure } from '~/components-v2/figure/video-figure'
import {
  BlogCtaModule,
  type BlogCtaVariant,
} from '~/components-v2/blog/blog-cta-module'
import { isCanonicalLink } from './is-canonical-link'

const embeddedEntryToComponent = (node: Block | Inline) => {
  switch (node.data.target.sys.contentType.sys.id) {
    /**
     * Object is known as "Video"
     */
    case 'code':
      return (
        <CodeBlock language={node.data.target.fields.language[0]}>
          {node.data.target.fields.code}
        </CodeBlock>
      )
    /**
     * Object is known as "Video"
     */
    case 'demo':
      return (
        <VideoFigure
          description={node.data.target.fields.description}
          title={node.data.target.fields.title}
          src={node.data.target.fields.videoUrl}
        />
      )
    case 'quote':
      return (
        <Quote cite={node.data.target.fields.source}>
          {node.data.target.fields.quote}
        </Quote>
      )

    default:
      return (
        <p>
          No handler available for entry type:
          {node.data.target.sys.contentType.sys.id}
        </p>
      )
  }
}

/**
 * Blog posts frame demo videos, but otherwise fall back to the shared handler.
 * Used for both the inline and block flavours of an embedded entry.
 */
const blogPostEmbeddedEntryToComponent = (node: Block | Inline) => {
  switch (node.data.target.sys.contentType.sys.id) {
    case 'demo':
      return (
        <div className="image-frame">
          <VideoFigure
            description={node.data.target.fields.description}
            title={node.data.target.fields.title}
            src={node.data.target.fields.videoUrl}
          />
        </div>
      )

    case 'engineeringBlogCta': {
      const { variant } = node.data.target.fields

      return <BlogCtaModule variant={variant as BlogCtaVariant} />
    }
  }

  return embeddedEntryToComponent(node)
}

const skipParagraphNodeTypes = new Set([
  'embedded-asset-block',
  'embedded-entry-inline',
])

const stringToAnchorSlug = (input: string) =>
  // Step 1: Remove all non-alphanumeric characters except spaces
  // Step 2: Replace all sequences of spaces with a single dash
  input
    .toLowerCase()
    .replace(/[^a-z0-9 ]/g, ' ')
    .trim()
    .replace(/\s+/g, '-')

const nodeToText = (input: Text[]) =>
  input
    .filter(({ nodeType }) => nodeType === 'text')
    .map((node) => ('value' in node ? node.value : ''))
    .join(' ')

const TableOverflow = ({ children }: { children: React.ReactNode }) => {
  return <div className="table-overflow">{children}</div>
}

const renderNodeCommon: Options['renderNode'] = {
  [BLOCKS.HEADING_2]: (node, children) => {
    const anchor = stringToAnchorSlug(nodeToText(node.content as Text[]))
    return (
      <h2 id={anchor}>
        {children} <AnchorReveal anchor={anchor} />
      </h2>
    )
  },
  [BLOCKS.HEADING_3]: (node, children) => {
    const anchor = stringToAnchorSlug(nodeToText(node.content as Text[]))
    return (
      <h3 id={anchor}>
        {children} <AnchorReveal anchor={anchor} />
      </h3>
    )
  },
  [BLOCKS.PARAGRAPH]: (node, children) => {
    if (!children) return

    // if (node)
    if (
      node.content.find(({ nodeType }) => skipParagraphNodeTypes.has(nodeType))
    )
      return children

    return <p>{children}</p>
  },
  [INLINES.HYPERLINK]: ({ data }, children) => {
    const isCanonical = isCanonicalLink(data.uri)

    return (
      <a
        href={data.uri}
        target={isCanonical ? undefined : '_blank'}
        rel={isCanonical ? undefined : 'noopener noreferrer'}
      >
        {children}
      </a>
    )
  },
}

export const renderOption: Options = {
  renderNode: {
    ...renderNodeCommon,
    [BLOCKS.EMBEDDED_ASSET]: (node) => {
      return <Figure {...node.data.target} tags={node.data} />
    },
    [INLINES.EMBEDDED_ENTRY]: (node) => {
      // target the contentType of the EMBEDDED_ENTRY to display as you need
      return embeddedEntryToComponent(node)
    },
    [BLOCKS.EMBEDDED_ENTRY]: (node) => {
      return embeddedEntryToComponent(node)
    },
  },
}

export const blogPostRenderOption: Options = {
  renderNode: {
    ...renderNodeCommon,
    [BLOCKS.QUOTE]: (_node, children) => {
      return <blockquote className="inline-quote">{children}</blockquote>
    },
    [BLOCKS.TABLE]: (_node, children) => {
      return (
        <TableOverflow>
          <table>
            <tbody>{children}</tbody>
          </table>
        </TableOverflow>
      )
    },
    [BLOCKS.EMBEDDED_ASSET]: (node) => {
      return (
        <Figure dimensions={false} {...node.data.target} tags={node.data} />
      )
    },
    [INLINES.EMBEDDED_ENTRY]: (node) => {
      // target the contentType of the EMBEDDED_ENTRY to display as you need
      return blogPostEmbeddedEntryToComponent(node)
    },
    [BLOCKS.EMBEDDED_ENTRY]: (node) => {
      return blogPostEmbeddedEntryToComponent(node)
    },
  },
}
